import ChatClient from '@/client/mentor/ChatClient'
import type {AxiosError} from 'axios'
import type {MaybeRef} from 'vue'
import type {Maybe} from '@/types'
import type {Message, SystemMessage, UserMessage} from '@/client/mentor/MessagesClient'
import type {MessagePage, MessagesInfiniteData} from '@/composables/query/mentor/useAssistantMessages'
import type {MessageRequest} from '@/client/mentor/ChatClient'
import type {UserPrompt} from '@/components/assistant/chat/InputArea.vue'
import {AssistantMessageStatus, ConversationMessageType, ResponseFormat} from '@/client/mentor/MessagesClient'
import {ErrorCode} from '@/client/mentor/types'
import {
  FIRST_PAGE_INDEX,
  PENDING_ASSISTANT_MESSAGE_ID,
  PENDING_CONVERSATION_ID,
  PENDING_MESSAGE_ID_INCREMENT,
  PENDING_USER_MESSAGE_ID,
} from '@/components/assistant/constants'
import {buildMessageQueryKey} from '@/composables/query/mentor/useAssistantMessages'
import {ref, toValue} from 'vue'
import {useQueryClient, useMutation} from '@tanstack/vue-query'

interface ErrorResponse {
  conversationId?: string
  errorCode?: string
}

interface MutationContext {
  conversationId: Maybe<string>
  pendingAssistantMessageId?: number
  pendingUserMessageId?: number
}

export function useAssistantSendMessage(companyId: number, activeConversationId: MaybeRef<Maybe<string>>) {
  const queryClient = useQueryClient()
  const pendingMessages = ref<Message[]>([])

  const mutation = useMutation<Message, AxiosError<ErrorResponse>, UserPrompt, MutationContext>({
    mutationFn: async (userPrompt: UserPrompt) => {
      const conversationId = toValue(activeConversationId)

      const data: MessageRequest = {
        companyId,
        prompt: userPrompt.prompt,
        preferredFormat: userPrompt.preferredFormat,
        conversationId,
      }
      return ChatClient.sendMessage(data)
    },
    onMutate: (userPrompt): MutationContext => {
      const conversationId = toValue(activeConversationId)
      let pendingAssistantMessageId: number | undefined
      let pendingUserMessageId: number | undefined

      if (conversationId) {
        const queryKey = buildMessageQueryKey(companyId, conversationId)
        queryClient.setQueryData<MessagesInfiniteData | undefined>(queryKey, (data) => {
          if (!data) return data
          const highestMessageId = getHighestMessageId(data.pages)
          pendingUserMessageId = highestMessageId + PENDING_MESSAGE_ID_INCREMENT
          pendingAssistantMessageId = pendingUserMessageId + PENDING_MESSAGE_ID_INCREMENT

          return prependToNewestPageMany(data, [
            {
              id: pendingUserMessageId,
              conversationId,
              type: ConversationMessageType.USER,
              content: {
                type: ConversationMessageType.USER,
                prompt: userPrompt.prompt,
                preferredFormat: userPrompt.preferredFormat,
              },
              createdAt: new Date(),
            } satisfies UserMessage,
            {
              id: pendingAssistantMessageId,
              conversationId,
              type: ConversationMessageType.ASSISTANT,
              content: {
                type: ConversationMessageType.ASSISTANT,
                status: AssistantMessageStatus.PENDING,
                format: ResponseFormat.TEXT,
                answer: '',
              },
              createdAt: new Date(),
            },
          ])
        })
      } else {
        pendingMessages.value = [
          {
            id: PENDING_USER_MESSAGE_ID,
            conversationId: PENDING_CONVERSATION_ID,
            type: ConversationMessageType.USER,
            content: {type: ConversationMessageType.USER, prompt: userPrompt.prompt, preferredFormat: userPrompt.preferredFormat},
            createdAt: new Date(),
          },
          {
            id: PENDING_ASSISTANT_MESSAGE_ID,
            conversationId: PENDING_CONVERSATION_ID,
            type: ConversationMessageType.ASSISTANT,
            content: {
              type: ConversationMessageType.ASSISTANT,
              status: AssistantMessageStatus.PENDING,
              format: ResponseFormat.TEXT,
              answer: '',
            },
            createdAt: new Date(),
          },
        ]
      }

      return {conversationId, pendingAssistantMessageId, pendingUserMessageId}
    },
    onError: (error, __, context) => {
      const conversationId = context?.conversationId
      const errorCode = error?.response?.data?.errorCode ?? ErrorCode.SEND_MESSAGE_FAILED

      if (conversationId) {
        const pendingAssistantMessageId = context.pendingAssistantMessageId
        const queryKey = buildMessageQueryKey(companyId, conversationId)
        queryClient.setQueryData<MessagesInfiniteData | undefined>(queryKey, (data) => {
          if (!data) return data

          const errorMessage: SystemMessage = {
            id: pendingAssistantMessageId ?? getHighestMessageId(data.pages) + PENDING_MESSAGE_ID_INCREMENT,
            conversationId,
            type: ConversationMessageType.SYSTEM,
            content: {type: ConversationMessageType.SYSTEM, errorCode},
            createdAt: new Date(),
          }

          if (pendingAssistantMessageId) {
            return replaceMessage(data, pendingAssistantMessageId, errorMessage)
          }

          return prependToNewestPage(data, errorMessage)
        })
      } else {
        pendingMessages.value = pendingMessages.value.map((message) => {
          if (message.id !== PENDING_ASSISTANT_MESSAGE_ID) return message

          return {
            id: PENDING_ASSISTANT_MESSAGE_ID,
            conversationId: PENDING_CONVERSATION_ID,
            type: ConversationMessageType.SYSTEM,
            content: {type: ConversationMessageType.SYSTEM, errorCode},
            createdAt: new Date(),
          } satisfies SystemMessage
        })
      }
    },
    onSuccess: (newMessage, _, context) => {
      pendingMessages.value = []

      const queryKey = buildMessageQueryKey(companyId, newMessage.conversationId)
      queryClient.setQueryData<MessagesInfiniteData | undefined>(queryKey, (data) => {
        if (!data) return data

        const pendingAssistantMessageId = context?.pendingAssistantMessageId
        if (pendingAssistantMessageId) {
          return replaceMessage(data, pendingAssistantMessageId, newMessage)
        }

        return prependToNewestPage(data, newMessage)
      })
    },
  })

  return {
    sendMessage: mutation.mutateAsync,
    isSending: mutation.isPending,
    pendingMessages,
  }
}

function getHighestMessageId(items: MessagePage[]) {
  return items.flatMap((page) => page.items).reduce((maxId, message) => Math.max(maxId, message.id), 0)
}

function prependToNewestPage(data: MessagesInfiniteData, message: Message): MessagesInfiniteData {
  return prependToNewestPageMany(data, [message])
}

function prependToNewestPageMany(data: MessagesInfiniteData, messages: Message[]): MessagesInfiniteData {
  const pages = [...data.pages]
  if (pages.length === 0) return data

  const newestPageIndex = FIRST_PAGE_INDEX
  const newestPage = pages[newestPageIndex]

  pages[newestPageIndex] = {
    ...newestPage,
    items: [...messages, ...newestPage.items],
  }

  return {...data, pages}
}

export function replaceMessage(data: MessagesInfiniteData, targetMessageId: number, replacement: Message): MessagesInfiniteData {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => (item.id === targetMessageId ? replacement : item)),
    })),
  }
}
